// src/components/Insights.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types'; // Import PropTypes
import './Insights.css';
import { firestore } from '../../firebase';
import { collection, doc, setDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import SQLFirestore from '../../SQLFirestore';
import CustomModal from '../Common/CustomModal'; // Import the custom modal

const Insights = ({ showToast, onClose }) => {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataContext, setDataContext] = useState('');
  const [results, setResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');

  const sqlFirestore = new SQLFirestore();

  const handleExecute = async (sqlQuery) => {
    setLoading(true);
    setError('');
    try {
      const ast = sqlFirestore.parseSQL(sqlQuery);
      if (ast.groupby && ast.groupby.columns.length > 0) {
        const grouped = await sqlFirestore.executeGroupedQuery(sqlQuery);
        setResults(grouped);
      } else if (sqlFirestore.hasAggregation(ast)) {
        const aggregated = await sqlFirestore.executeQuery(sqlQuery);
        setResults(aggregated);
      } else {
        const data = await sqlFirestore.executeQuery(sqlQuery);
        setResults(data);
        console.log(data);
      }
    } catch (err) {
      setError(err.message);
      showToast(`Error ejecutando la consulta: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('Usuario no autenticado');
        const recordsCollection = collection(firestore, 'records');
        const recordsSnapshot = await getDocs(recordsCollection);
        if (!recordsSnapshot.empty) {
          const firstDocData = recordsSnapshot.docs[0].data();
          const schema = Object.keys(firstDocData).reduce((acc, key) => {
            acc[key] = typeof firstDocData[key];
            return acc;
          }, {});
          setDataContext(JSON.stringify(schema));
        }
      } catch (error) {
        setError(error.message);
        showToast(`Error al obtener el esquema: ${error.message}`, 'error');
      }
    };
    fetchSchema();
  }, [showToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) {
      showToast('Por favor, ingresa una pregunta.', 'danger');
      return;
    }
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Usuario no autenticado');

      const newMessages = [...messages, { sender: 'user', text: question }];
      setMessages(newMessages);

      const conversationRef = doc(firestore, 'insights', user.uid);
      await setDoc(conversationRef, { messages: newMessages });

      const requestBody = { question: question, schema: dataContext };
      const FUNCTION_URL = 'https://generate-insight-xv2ujygbla-uc.a.run.app';
      const idToken = await user.getIdToken();

      const response = await axios.post(FUNCTION_URL, requestBody, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      });

      const aiResponse = response.data;
      const parsedResponse = JSON.parse(aiResponse.query);
      await handleExecute(parsedResponse.query);

      const updatedMessages = [
        ...newMessages,
        { sender: 'ai', text: 'Consulta ejecutada exitosamente.', showViewButton: true },
      ];
      setMessages(updatedMessages);

      await setDoc(conversationRef, { messages: updatedMessages });
      setQuestion('');

      showToast('Consulta ejecutada exitosamente.', 'success');
    } catch (error) {
      setMessages([...messages, { sender: 'ai', text: 'Error generando la respuesta.' }]);
      setError(error.message || 'Ocurrió un error al generar la respuesta.');
      showToast(`Error al generar la respuesta: ${error.message || error}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClearConversation = async () => {
    setMessages([]);
    setQuestion('');
    setError('');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Usuario no autenticado');
      const conversationRef = doc(firestore, 'insights', user.uid);
      await deleteDoc(conversationRef);
      showToast('Conversación limpiada correctamente.', 'success');
    } catch (error) {
      setError(error.message);
      showToast(`Error al limpiar la conversación: ${error.message}`, 'error');
    }
  };

  const handleViewClick = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="insights-container">
      <h2>Chat de Insights</h2>
      <div className="insights-chat-window">
        {messages.map((msg, index) => (
          <div key={index} className={`insights-message ${msg.sender}`}>
            <p>{msg.text}</p>
            {msg.showViewButton && (
              <button className="view-button" onClick={handleViewClick} aria-label="Ver resultados">
                Ver
              </button>
            )}
          </div>
        ))}
        {loading && (
          <div className="insights-message ai">
            <div className="spinner"></div>
            <p>Generando respuesta...</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="insights-form">
        <textarea
          className="insights-textarea"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ingresa tu pregunta aquí..."
          rows={2}
          disabled={loading}
          aria-label="Área de texto para preguntas"
        />
        <button type="submit" className="insights-submit-button" disabled={loading}>
          {loading ? (
            <>
              <div className="spinner"></div>
              Enviando...
            </>
          ) : (
            'Enviar'
          )}
        </button>
      </form>

      <button onClick={handleClearConversation} className="insights-clear-button">
        Limpiar Conversación
      </button>

      {/* Display error message if any */}
      {error && <p className="error-message">Error: {error}</p>}

      {/* Custom Modal for Query Results */}
      <CustomModal show={showModal} onClose={closeModal} title="Resultados de la Consulta">
        {results.length > 0 ? (
          <table className="insights-results-table">
            <thead>
              <tr>
                {Object.keys(results[0]).map((key) => (
                  <th key={key}>{key.charAt(0).toUpperCase() + key.slice(1)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr key={index}>
                  {Object.values(result).map((value, idx) => (
                    <td key={idx}>{typeof value === 'object' ? JSON.stringify(value) : value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No hay resultados para mostrar.</p>
        )}
      </CustomModal>
    </div>
  );
};

// Define PropTypes for type checking
Insights.propTypes = {
  showToast: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Insights;
